import { Check, Apple } from "lucide-react";
import { PLAY_STORE_URL } from "@/config";

const TIERS = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    scans: "1 scan per day",
    features: ["Full feature access", "AI identification & pricing", "Listings for all 3 marketplaces"],
    badge: "Free",
    highlight: false,
    testId: "pricing-free",
  },
  {
    name: "Starter",
    price: "$6.99",
    period: "/month",
    altPrice: "or $64.99/year (≈ $5.42/mo)",
    scans: "100 scans per month",
    features: ["Everything in Free", "100 scans per month", "Priority AI processing"],
    highlight: false,
    testId: "pricing-starter",
  },
  {
    name: "Pro",
    price: "$13.99",
    period: "/month",
    altPrice: "or $129.99/year (≈ $10.83/mo)",
    scans: "250 scans per month",
    features: ["Everything in Starter", "250 scans per month", "Advanced sold insights"],
    highlight: true,
    testId: "pricing-pro",
  },
];

const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="section-anchor border-t border-divider bg-surface2/40 py-24">
    <div className="mx-auto max-w-6xl px-4 sm:px-6">
      <p className="font-display text-sm font-semibold uppercase tracking-widest text-brand-light">Pricing</p>
      <h2 className="mt-3 font-display text-3xl font-bold uppercase tracking-wide text-onsurface sm:text-4xl">
        Start free. Upgrade when you're selling more.
      </h2>
      <p className="mt-4 max-w-xl text-sm text-onsurface2">
        Subscriptions are handled securely in the app through Google Play — no checkout on this site.
      </p>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {TIERS.map(({ name, price, period, altPrice, scans, features, badge, highlight, testId }) => (
          <div
            key={testId}
            data-testid={testId}
            className={`relative flex flex-col rounded-lg border p-8 ${
              highlight ? "border-brand bg-surface2 shadow-cta" : "border-line bg-surface2"
            }`}
          >
            {badge && (
              <span className="absolute -top-3 left-6 rounded-full bg-success px-3 py-1 font-display text-xs font-bold uppercase tracking-wider text-surface">
                {badge}
              </span>
            )}
            {highlight && (
              <span className="absolute -top-3 left-6 rounded-full bg-brand px-3 py-1 font-display text-xs font-bold uppercase tracking-wider text-white">
                Most popular
              </span>
            )}
            <h3 className="font-display text-xl font-semibold uppercase tracking-wide text-onsurface">{name}</h3>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-4xl font-bold text-onsurface">{price}</span>
              <span className="text-sm text-onsurface2">{period}</span>
            </div>
            {altPrice && <p className="mt-1 text-xs text-brand-light">{altPrice}</p>}
            <p className="mt-4 rounded-full bg-brand-tint px-3 py-1 text-center text-xs font-semibold text-brand-tinttext">{scans}</p>
            <ul className="mt-6 flex-1 space-y-3">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-onsurface2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {f}
                </li>
              ))}
            </ul>
            <a
              data-testid={`${testId}-cta`}
              href={PLAY_STORE_URL}
              className={`mt-8 rounded-lg py-3 text-center font-display text-sm font-bold uppercase tracking-wider transition-shadow duration-200 ${
                highlight
                  ? "bg-brand text-white shadow-cta hover:shadow-cta-lg"
                  : "border border-linestrong text-onsurface hover:border-brand-light hover:text-brand-light"
              }`}
            >
              Get it on Google Play
            </a>
            <span
              data-testid={`${testId}-app-store-soon`}
              className="mt-3 inline-flex cursor-not-allowed select-none items-center justify-center gap-1.5 rounded-lg border border-line py-2 text-xs text-onsurface2 opacity-60"
            >
              <Apple className="h-3.5 w-3.5" /> App Store — coming soon
            </span>
          </div>
        ))}
      </div>

      <p data-testid="pricing-trial-note" className="mt-8 text-center text-xs text-onsurface2">
        3-day free trial for eligible new subscribers. Cancel anytime in Google Play.
      </p>
    </div>
  </section>
);

export default Pricing;
